import { differenceInCalendarDays, isLeapYear } from "date-fns";
import type { Identifier } from "ra-core";

import type { Client } from "../types";

/** Window size (Almikia Epic E3, TASK-024): today through the next 30 days, inclusive. */
export const UPCOMING_BIRTHDAYS_WINDOW_DAYS = 30;

/** Row cap for the dashboard card (ASSUMPTION in the ticket). */
export const UPCOMING_BIRTHDAYS_MAX_ROWS = 10;

/** The only client fields the window computation needs. */
export type UpcomingBirthdayClient = Pick<
  Client,
  "id" | "full_legal_name" | "birthday"
>;

export type UpcomingBirthday = {
  id: Identifier;
  full_legal_name: string;
  /** 1-12, the month of the next occurrence (not necessarily the birth month for a 29 Feb edge case). */
  month: number;
  /** Day of the next occurrence. */
  day: number;
  /** 0-30, days from `today` to the next occurrence, inclusive of both ends. */
  daysUntil: number;
};

/**
 * Parses a `birthday` stored as `yyyy-MM-dd` into its month/day, ignoring the
 * year and avoiding any timezone shift (string split, no `Date` parsing of
 * the full string — see ClientShow.tsx's note on UTC vs local offsets).
 */
const parseMonthDay = (birthday: string): { month: number; day: number } => {
  const [, monthStr, dayStr] = birthday.split("-");
  return { month: Number(monthStr), day: Number(dayStr) };
};

/**
 * The calendar date a birthday's month/day falls on in a given year. A 29
 * February birthday shows on 28 February in a non-leap year (ASSUMPTION).
 */
const occurrenceDate = (month: number, day: number, year: number): Date => {
  const isFeb29 = month === 2 && day === 29;
  const effectiveDay = isFeb29 && !isLeapYear(new Date(year, 0, 1)) ? 28 : day;
  return new Date(year, month - 1, effectiveDay);
};

/**
 * Computes, for a single client, the next occurrence of their birthday
 * relative to `today` (today counts as day 0) — handling the December to
 * January wrap by rolling over to next year when this year's occurrence has
 * already passed.
 */
const nextOccurrence = (
  month: number,
  day: number,
  today: Date,
): { month: number; day: number; daysUntil: number } => {
  const todayYear = today.getFullYear();
  let candidate = occurrenceDate(month, day, todayYear);
  let daysUntil = differenceInCalendarDays(candidate, today);

  if (daysUntil < 0) {
    candidate = occurrenceDate(month, day, todayYear + 1);
    daysUntil = differenceInCalendarDays(candidate, today);
  }

  return {
    month: candidate.getMonth() + 1,
    day: candidate.getDate(),
    daysUntil,
  };
};

/**
 * Filters and sorts clients to the ones whose next birthday falls within
 * `[today, today + UPCOMING_BIRTHDAYS_WINDOW_DAYS]` (both ends inclusive),
 * soonest first, capped at `UPCOMING_BIRTHDAYS_MAX_ROWS`. Clients without a
 * birthday are ignored. Pure function — no data fetching, no i18n, no PII
 * leaves this module.
 */
export const computeUpcomingBirthdays = (
  clients: UpcomingBirthdayClient[],
  today: Date = new Date(),
): UpcomingBirthday[] =>
  clients
    .filter((client): client is UpcomingBirthdayClient & { birthday: string } =>
      Boolean(client.birthday),
    )
    .map((client) => {
      const { month, day } = parseMonthDay(client.birthday);
      const next = nextOccurrence(month, day, today);
      return {
        id: client.id,
        full_legal_name: client.full_legal_name,
        month: next.month,
        day: next.day,
        daysUntil: next.daysUntil,
      };
    })
    .filter(
      (row) =>
        row.daysUntil >= 0 && row.daysUntil <= UPCOMING_BIRTHDAYS_WINDOW_DAYS,
    )
    .sort((a, b) => a.daysUntil - b.daysUntil)
    .slice(0, UPCOMING_BIRTHDAYS_MAX_ROWS);
